import type { Product, ProductBillOfMaterial } from '@ztechapps/common.contract'
import type { BomInventory } from './getBomInventory'
import { getBomInventory } from './getBomInventory'
import sumBy from 'lodash/sumBy'
import { quickClone } from '@ztechapps/common.utils'
import cloneDeep from 'lodash/cloneDeep'

export type ComputeBomOptions = {
  requiredOnly?: boolean
  inventories?: Map<string, BomInventory>
}

/**
 * Greedy strategy of computing BOM. It will consume all available quantity on hand for each product in the BOM.
 * It should return the product with the computed required quantity
 * @param bom
 * @param productId
 * @param qtyToBuild
 */
export function computeBomAsGreedy(product: Product, qtyToBuild: number, options?: ComputeBomOptions): Product {
  //becase it traverses and updates the object, we need to clone the bom

  const requiredOnly = options?.requiredOnly ?? true

  const mainProduct = cloneDeep({
    ...product,
    requiredComputedQuantityOnHand: qtyToBuild,
  }) as Product

  const mainBom = {
    product: mainProduct,
    quantity: 1,
    requiredToBuild: true,
  } as ProductBillOfMaterial

  //loop thru all products to get the current inventories, use the provided one if available
  const inventories: Map<string, BomInventory> = options?.inventories ?? getBomInventory(mainProduct)

  function traverse(item: ProductBillOfMaterial, numberOfItem: number) {
    const itemProduct = item.product as Product

    //ignore if not required to build
    if (requiredOnly && !item.requiredToBuild) {
      return
    }

    const totalQtyNeeded = item.quantity * numberOfItem

    // Use inventories if possible
    const inventory = inventories.get(itemProduct._id)

    const available = inventory?.qtyOnHand ?? 0

    //this is the greedy part, it tries to consume as much as possible,
    const sharedCount = Math.min(available, totalQtyNeeded)

    //the difference is only the quantity needed to build
    const qtyNeededToBuild = totalQtyNeeded - sharedCount

    //we need to update the inventories with the new available count, by substracting the original available count and the one you shared
    const newAvailable = available - sharedCount

    // Update inventories, to capture the remaining quantity
    inventories.set(itemProduct._id, {
      ...inventory,
      qtyOnHand: newAvailable,
    })

    //update the qty needed to build for the bom
    itemProduct.requiredComputedQuantityOnHand = qtyNeededToBuild

    //update the running inventories and te shared count
    item.runningInventory = newAvailable
    item.sharedInventory = sharedCount

    // we need to process it recursively
    //remove the items that is not required to build
    const childBom = itemProduct.billOfMaterials
    if (childBom?.length) {
      for (const child of childBom) {
        traverse(child, qtyNeededToBuild)
      }
    }

    computeTotalManufactureCost(itemProduct, qtyNeededToBuild)
    item.product = itemProduct //update the product
  }

  //start traversing the bom
  traverse(mainBom, qtyToBuild)

  if (requiredOnly) {
    mainProduct.billOfMaterials = removeNonRequiredItems(mainProduct.billOfMaterials)
  }

  //this will update the costing for the main product
  // computeTotalManufactureCost(mainProduct, qtyToBuild)
  return mainProduct
}

/**
 * This will compute the costing of the product or the totalManufactureCost
 * We just need to add miscellaneous cost + operation cost + sub-components totalManufactureCost
 * @param product
 * @param qtyNeededToBuild
 */
function computeTotalManufactureCost(product: Product, qtyNeededToBuild: number) {
  //we need to get teh total of operation cose and return zero if there is no operation
  //we need to multiply the quantity needed
  product.totalOperationsCost = computeTotalOperationCost(product) * qtyNeededToBuild

  product.miscellaneousCost = (product?.miscellaneousCost ?? 0) * qtyNeededToBuild

  //the sub-components don't need to be multiplied because the child cost already carried this out
  product.totalSubComponentsCost =
    sumBy(product?.billOfMaterials, ({ product }) => product?.totalManufactureCost ?? 0) ?? 0

  //this is the sum miscellaneous cost + operation cost + sub-components totalManufactureCost
  product.totalManufactureCost =
    product.totalOperationsCost + product.miscellaneousCost + product.totalSubComponentsCost
}

function removeNonRequiredItems(bomList: ProductBillOfMaterial[] | undefined): ProductBillOfMaterial[] {
  if (!bomList) return []
  const bomListClone = quickClone(bomList)

  return bomListClone
    .filter((item) => item.requiredToBuild)
    .map((item) => {
      if (item.product?.billOfMaterials?.length) {
        item.product.billOfMaterials = removeNonRequiredItems(item.product.billOfMaterials)
      }
      return item
    })
}

export function computeTotalOperationCost(product: Product): number {
  return sumBy(product?.operations, (e) => e?.total ?? 0) ?? 0
}
